import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
  FiPower,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiShield,
  FiMessageSquare,
  FiClock,
  FiCheckCircle,
  FiX,
  FiPackage,
  FiUsers,
  FiTrendingUp,
  FiPhoneCall,
  FiZap,
  FiAward,
  FiHeart,
  FiFilter,
  FiStar,
} from "react-icons/fi";
import { useToast } from "@chakra-ui/react";
import ConfirmModal from "../components/ConformModel/ConfirmModal";
import { getUserRole } from "../components/utilities/getUserRole";

const INITIAL_FORM_STATE = {
  name: "",
  category: "Entry",
  price: "",
  originalPrice: "",
  currency: "INR",
  isPopular: false,
  isActive: true,
  displayOrder: 0,
  stripePriceId: "",
  razorpayPlanId: "",

  // Entry Category
  durationInDays: 30,
  forGender: "all",
  unlimitedChats: false,
  unlimitedLikes: false,
  personalizedPremiumMatchesPerDay: 0,
  profileBoostersPerWeek: 0,

  // Call Pearls Category
  callTypeFocus: "Audio",
  pearlsToCredit: 700,
  estimatedMinutes: 10,
  pearlsPerMinuteRequired: 70,

  // Boost Badge Category
  badgeTier: "Super",
  boostQuantity: 5,
  durationPerBoostInDays: 3,
  visibleBoostBadge: true,
  increasedProfileVisibility: true,
  discoverPlacement: "priority",
  boostAnalytics: "standard",
  matchOpportunities: "standard",

  // Perfect Match Category
  dailyPerfectMatchAllowance: 5,
  priorityMatching: true,
  advancedFilters: true,
};

const SubscriptionPlans = () => {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeCycle, setActiveCycle] = useState("all");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const currentUser = getUserRole() || {};
  const isSuperAdmin = currentUser.role === "super-admin";

  const [form, setForm] = useState(INITIAL_FORM_STATE);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/v1/admin/subscriptions");
      const list = res.data?.data?.subscriptions || [];
      setPlans(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Fetch plans error:", error);
      toast({
        title: "Error loading subscriptions",
        description: error.response?.data?.message || "Failed to fetch subscription plans",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const filteredPlans = useMemo(() => {
    return plans.filter((p) => {
      if (selectedCategory !== "all" && p.category !== selectedCategory) {
        return false;
      }

      if (p.category === "Entry" && activeCycle !== "all") {
        const cycleMap = { weekly: 7, monthly: 30, quarterly: 90, yearly: 365 };
        const daysInCycle = cycleMap[activeCycle];
        const details = p.details || p.planDetails || {};
        const durationInDays = details.durationInDays || 30;
        return Math.abs(durationInDays - daysInCycle) < 5;
      }

      return true;
    });
  }, [plans, selectedCategory, activeCycle]);

  const stats = useMemo(() => {
    const total = plans.length;
    const active = plans.filter((p) => p.isActive).length;
    const avgPrice =
      total > 0
        ? Math.round(
            plans.reduce((acc, p) => acc + Number(p.price || 0), 0) / total
          )
        : 0;
    return { total, active, avgPrice };
  }, [plans]);

  const openAddModal = () => {
    setEditingId(null);
    setForm({
      ...INITIAL_FORM_STATE,
      category: selectedCategory !== "all" ? selectedCategory : "Entry",
    });
    setModalOpen(true);
  };

  const openEditModal = (pkg) => {
    const details = pkg.details || pkg.planDetails || {};
    const features = details.features || {};

    setEditingId(pkg._id);
    setForm({
      name: pkg.name || "",
      category: pkg.category || "Entry",
      price: pkg.price ?? "",
      originalPrice: pkg.originalPrice ?? "",
      currency: pkg.currency || "INR",
      isPopular: !!pkg.isPopular,
      isActive: pkg.isActive ?? true,
      displayOrder: pkg.displayOrder || 0,
      stripePriceId: pkg.stripePriceId || "",
      razorpayPlanId: pkg.razorpayPlanId || "",

      // Entry
      durationInDays: details.durationInDays || 30,
      forGender: pkg.forGender || "all",
      unlimitedChats: features.unlimitedChats ?? false,
      unlimitedLikes: features.unlimitedLikes ?? false,
      personalizedPremiumMatchesPerDay: features.personalizedPremiumMatchesPerDay || 0,
      profileBoostersPerWeek: features.profileBoostersPerWeek || 0,

      // Call Pearls
      callTypeFocus: details.callTypeFocus || "Audio",
      pearlsToCredit: details.pearlsToCredit || 0,
      estimatedMinutes: details.estimatedMinutes || 0,
      pearlsPerMinuteRequired: details.pearlsPerMinuteRequired || 0,

      // Boost Badge
      badgeTier: details.badgeTier || "Super",
      boostQuantity: details.boostQuantity || 1,
      durationPerBoostInDays: details.durationPerBoostInDays || 1,
      visibleBoostBadge: features.visibleBoostBadge ?? true,
      increasedProfileVisibility: features.increasedProfileVisibility ?? true,
      discoverPlacement: features.discoverPlacement || "priority",
      boostAnalytics: features.boostAnalytics || "standard",
      matchOpportunities: features.matchOpportunities || "standard",

      // Perfect Match
      dailyPerfectMatchAllowance: details.dailyPerfectMatchAllowance || 5,
      priorityMatching: features.priorityMatching ?? true,
      advancedFilters: features.advancedFilters ?? true,
    });
    setModalOpen(true);
  };

  const handleSavePlan = async () => {
    if (!form.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Plan name is required",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!form.price || Number(form.price) <= 0) {
      toast({
        title: "Validation Error",
        description: "Provide a valid price",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    let planDetailsData = {};

    if (form.category === "Entry") {
      planDetailsData = {
        durationInDays: Number(form.durationInDays || 30),
        features: {
          unlimitedChats: Boolean(form.unlimitedChats),
          unlimitedLikes: Boolean(form.unlimitedLikes),
          personalizedPremiumMatchesPerDay: Number(form.personalizedPremiumMatchesPerDay || 0),
          profileBoostersPerWeek: Number(form.profileBoostersPerWeek || 0),
        },
      };
    } else if (form.category === "Call_Pearls") {
      planDetailsData = {
        callTypeFocus: form.callTypeFocus,
        pearlsToCredit: Number(form.pearlsToCredit || 0),
        estimatedMinutes: Number(form.estimatedMinutes || 0),
        pearlsPerMinuteRequired: Number(form.pearlsPerMinuteRequired || 0),
      };
    } else if (form.category === "Boost_Badge") {
      planDetailsData = {
        badgeTier: form.badgeTier,
        boostQuantity: Number(form.boostQuantity || 1),
        durationPerBoostInDays: Number(form.durationPerBoostInDays || 1),
        features: {
          visibleBoostBadge: Boolean(form.visibleBoostBadge),
          increasedProfileVisibility: Boolean(form.increasedProfileVisibility),
          discoverPlacement: form.discoverPlacement,
          boostAnalytics: form.boostAnalytics,
          matchOpportunities: form.matchOpportunities,
        },
      };
    } else if (form.category === "Perfect_Match") {
      planDetailsData = {
        durationInDays: Number(form.durationInDays || 30),
        dailyPerfectMatchAllowance: Number(form.dailyPerfectMatchAllowance || 5),
        features: {
          priorityMatching: Boolean(form.priorityMatching),
          advancedFilters: Boolean(form.advancedFilters),
        },
      };
    }

    const payload = {
      name: form.name,
      category: form.category,
      price: Number(form.price),
      originalPrice: form.originalPrice ? Number(form.originalPrice) : Number(form.price),
      currency: form.currency,
      forGender: form.forGender,
      isActive: form.isActive,
      isPopular: form.isPopular,
      displayOrder: Number(form.displayOrder || 0),
      stripePriceId: form.stripePriceId || null,
      razorpayPlanId: form.razorpayPlanId || null,
      planDetails: planDetailsData,
      details: planDetailsData,
    };

    try {
      if (editingId) {
        await axios.patch(`/api/v1/admin/subscriptions/${editingId}`, {
          subscriptionData: {
            name: payload.name,
            price: payload.price,
            originalPrice: payload.originalPrice,
            currency: payload.currency,
            forGender: form.forGender,
            isActive: payload.isActive,
            isPopular: payload.isPopular,
            displayOrder: payload.displayOrder,
            stripePriceId: payload.stripePriceId,
            razorpayPlanId: payload.razorpayPlanId,
          },
          planDetails: planDetailsData,
          details: planDetailsData,
        });
        toast({
          title: "Success",
          description: "Subscription plan updated successfully!",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      } else {
        await axios.post("/api/v1/admin/subscriptions", payload);
        toast({
          title: "Success",
          description: "Subscription plan created successfully!",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      }
      await fetchPlans();
      setModalOpen(false);
    } catch (error) {
      console.error("Save error:", error);
      const errorMsg = error.response?.data?.message || "Failed to save subscription plan";
      toast({
        title: "Error",
        description: errorMsg,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePlan = async (plan) => {
    const endpoint = plan.isActive ? "deactivate" : "activate";
    try {
      await axios.patch(`/api/v1/admin/subscriptions/${plan._id}/${endpoint}`);
      toast({
        title: "Success",
        description: `Plan ${plan.isActive ? "deactivated" : "activated"} successfully!`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await fetchPlans();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to toggle plan status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeletePlan = async (plan) => {
    setSaving(true);
    try {
      await axios.delete(`/api/v1/admin/subscriptions/${plan._id}`);
      toast({
        title: "Success",
        description: "Subscription plan deleted successfully!",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchPlans();
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to delete plan";
      toast({
        title: "Error",
        description: errorMsg,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setConfirmModalOpen(false);
      setPlanToDelete(null);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative flex items-center justify-center">
          <div className="w-14 h-14 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          <div className="absolute inset-0 rounded-full blur-md bg-cyan-400/25 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-cyan-50 p-2 md:p-4">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-2 border-b border-cyan-500/20">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white">
            Subscription Plans & Store
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Manage Entry memberships, Call Pearl top-ups, and Profile Boost badges.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:brightness-110 transition"
        >
          <FiPlus size={16} /> Create Plan
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <span className="text-xs uppercase font-bold text-cyan-300/70">Total Plans</span>
          <p className="text-2xl font-black text-white mt-1">{stats.total}</p>
        </div>
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <span className="text-xs uppercase font-bold text-teal-300/70">Live Available</span>
          <p className="text-2xl font-black text-teal-300 mt-1">{stats.active}</p>
        </div>
        <div className="p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 backdrop-blur-xl">
          <span className="text-xs uppercase font-bold text-sky-300/70">Average Price</span>
          <p className="text-2xl font-black text-sky-300 mt-1">₹{stats.avgPrice}</p>
        </div>
      </div>

      {/* Filters: Category & Billing Cycle */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "all", label: "All Categories" },
            { id: "Entry", label: "Entry Membership" },
            { id: "Call_Pearls", label: "Call Pearls" },
            { id: "Boost_Badge", label: "Boost Badges" },
            { id: "Perfect_Match", label: "Perfect Matchess" },
          ].map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`text-xs font-semibold px-3 py-2 rounded-xl border transition ${
                selectedCategory === cat.id
                  ? "bg-cyan-500/20 border-cyan-400 text-cyan-200 shadow-[0_0_12px_rgba(6,182,212,0.3)]"
                  : "border-cyan-500/20 text-cyan-300/60 hover:text-cyan-200 bg-cyan-950/20"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Sub-filter cycle (relevant when Entry plans are in view) */}
        {(selectedCategory === "all" || selectedCategory === "Entry") && (
          <div className="flex gap-1.5 items-center bg-cyan-950/50 p-1 rounded-xl border border-cyan-500/20 text-xs">
            <span className="text-[10px] uppercase font-bold text-cyan-400/60 px-2">Cycle:</span>
            {["all", "weekly", "monthly", "quarterly", "yearly"].map((cycle) => (
              <button
                key={cycle}
                onClick={() => setActiveCycle(cycle)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition ${
                  activeCycle === cycle
                    ? "bg-cyan-400 text-gray-950 font-bold"
                    : "text-cyan-200/60 hover:text-white"
                }`}
              >
                {cycle.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Plans List or Empty State */}
      {filteredPlans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="relative mb-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-500/30 flex items-center justify-center">
              <FiPackage className="text-cyan-400" size={40} />
            </div>
            <div className="absolute inset-0 rounded-full blur-xl bg-cyan-400/20 animate-pulse" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Plans Found</h3>
          <p className="text-sm text-cyan-200/60 text-center max-w-md mb-6">
            No subscription or top-up plans match the current filters. Create a new plan or reset your filters.
          </p>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:brightness-110 transition"
          >
            <FiPlus size={16} /> Create a Plan
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlans.map((pkg) => {
            const details = pkg.details || pkg.planDetails || {};
            const features = details.features || {};
            const hasOriginalPrice = pkg.originalPrice && pkg.originalPrice > pkg.price;
            const displayPrice = pkg.price;
            const originalPrice = pkg.originalPrice || pkg.price;
            const discountPercentage =
              pkg.discountPercentage ??
              Math.round(((originalPrice - displayPrice) / originalPrice) * 100);

            return (
              <div
                key={pkg._id}
                className={`rounded-3xl p-6 relative flex flex-col justify-between border backdrop-blur-xl transition duration-300 hover:-translate-y-1.5 shadow-2xl ${
                  pkg.isActive
                    ? "border-cyan-400/30 bg-gradient-to-b from-[#061e47]/90 to-[#020b22]/95"
                    : "border-gray-700/50 bg-[#030a1c]/70 opacity-60"
                }`}
              >
                <div>
                  {/* Top Badges */}
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      {pkg.category === "Entry" && (
                        <span className="text-[10px] uppercase font-extrabold tracking-wider px-2.5 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 flex items-center gap-1">
                          <FiClock size={11} /> {details.durationInDays || 30} days
                        </span>
                      )}
                      {pkg.category === "Call_Pearls" && (
                        <span className="text-[10px] uppercase font-extrabold tracking-wider px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 flex items-center gap-1">
                          <FiPhoneCall size={11} /> {details.callTypeFocus || "Audio"} Call
                        </span>
                      )}
                      {pkg.category === "Boost_Badge" && (
                        <span className="text-[10px] uppercase font-extrabold tracking-wider px-2.5 py-1 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-300 flex items-center gap-1">
                          <FiZap size={11} /> Tier: {details.badgeTier || "Super"}
                        </span>
                      )}

                      <span className="text-[10px] uppercase font-extrabold tracking-wider px-2.5 py-1 rounded-full bg-purple-500/20 border border-purple-400/40 text-purple-300 flex items-center gap-1">
                        <FiUsers size={10} /> {pkg.category.replace("_", " ")}
                      </span>
                    </div>

                    {pkg.isPopular && (
                      <span className="text-[10px] uppercase font-extrabold px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-400 text-amber-300">
                        ★ Popular
                      </span>
                    )}
                  </div>

                  {/* Plan Name & Pricing */}
                  <h3 className="text-xl font-black text-white">{pkg.name}</h3>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-3xl font-extrabold text-cyan-300">₹{displayPrice}</span>
                    {hasOriginalPrice && (
                      <>
                        <span className="text-sm line-through text-cyan-200/40">₹{originalPrice}</span>
                        <span className="text-xs font-bold text-green-400">
                          {discountPercentage}% OFF
                        </span>
                      </>
                    )}
                  </div>

                  {/* Category-Specific Details Card */}
                  <div className="my-5 p-4 rounded-2xl bg-cyan-950/40 border border-cyan-500/15 space-y-2.5 text-xs">
                    {/* Entry Plan Details */}
                    {pkg.category === "Entry" && (
                      <>
                        {features.unlimitedChats && (
                          <div className="flex items-center gap-2.5 text-cyan-100">
                            <FiMessageSquare className="text-cyan-400" size={14} />
                            <span>Unlimited Chats</span>
                          </div>
                        )}
                        {features.unlimitedLikes && (
                          <div className="flex items-center gap-2.5 text-cyan-100">
                            <FiCheckCircle className="text-cyan-400" size={14} />
                            <span>Unlimited Likes</span>
                          </div>
                        )}
                        {features.personalizedPremiumMatchesPerDay > 0 && (
                          <div className="flex items-center gap-2.5 text-teal-300">
                            <FiShield className="text-teal-400" size={14} />
                            <span>{features.personalizedPremiumMatchesPerDay} Premium Matches/Day</span>
                          </div>
                        )}
                        {features.profileBoostersPerWeek > 0 && (
                          <div className="flex items-center gap-2.5 text-teal-300">
                            <FiTrendingUp className="text-teal-400" size={14} />
                            <span>{features.profileBoostersPerWeek} Profile Boosts/Week</span>
                          </div>
                        )}
                        {!features.unlimitedChats &&
                          !features.unlimitedLikes &&
                          !features.personalizedPremiumMatchesPerDay &&
                          !features.profileBoostersPerWeek && (
                            <span className="text-cyan-200/50">Standard Entry access</span>
                          )}
                      </>
                    )}

                    {/* Call Pearls Plan Details */}
                    {pkg.category === "Call_Pearls" && (
                      <>
                        <div className="flex items-center gap-2.5 text-amber-200">
                          <FiAward className="text-amber-400" size={14} />
                          <span className="font-semibold">{details.pearlsToCredit || 0} Pearls Credited</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-cyan-100">
                          <FiPhoneCall className="text-cyan-400" size={14} />
                          <span>~{details.estimatedMinutes || 0} Minutes Talk Time</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-cyan-300/80">
                          <FiClock className="text-cyan-400" size={14} />
                          <span>{details.pearlsPerMinuteRequired || 0} Pearls / Min</span>
                        </div>
                      </>
                    )}

                    {/* Boost Badge Plan Details */}
                    {pkg.category === "Boost_Badge" && (
                      <>
                        <div className="flex items-center gap-2.5 text-rose-300">
                          <FiZap className="text-rose-400" size={14} />
                          <span className="font-semibold">{details.boostQuantity || 0} Boosts Included</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-cyan-100">
                          <FiClock className="text-cyan-400" size={14} />
                          <span>{details.durationPerBoostInDays || 0} Days per Boost</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-cyan-200/90">
                          <FiTrendingUp className="text-cyan-400" size={14} />
                          <span>Placement: {features.discoverPlacement || "Priority"}</span>
                        </div>
                        {features.visibleBoostBadge && (
                          <div className="flex items-center gap-2.5 text-teal-300">
                            <FiCheckCircle className="text-teal-400" size={14} />
                            <span>Visible Profile Badge</span>
                          </div>
                        )}
                      </>
                    )}

                    {/* Perfect Match Plan Details */}
                    {pkg.category === "Perfect_Match" && (
                      <>
                        <div className="flex items-center gap-2.5 text-pink-300">
                          <FiHeart className="text-pink-400" size={14} />
                          <span className="font-semibold">{details.dailyPerfectMatchAllowance || 0} Daily Perfect Calls</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-cyan-100">
                          <FiClock className="text-cyan-400" size={14} />
                          <span>Valid for {details.durationInDays || 30} days</span>
                        </div>
                        {features.priorityMatching && (
                          <div className="flex items-center gap-2.5 text-amber-300">
                            <FiStar className="text-amber-400" size={14} />
                            <span>Priority Matching Enabled</span>
                          </div>
                        )}
                        {features.advancedFilters && (
                          <div className="flex items-center gap-2.5 text-teal-300">
                            <FiFilter className="text-teal-400" size={14} />
                            <span>Advanced Filters</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="pt-4 border-t border-cyan-500/15 flex items-center justify-between">
                  <button
                    onClick={() => handleTogglePlan(pkg)}
                    className={`p-2 rounded-xl text-xs font-bold border transition ${
                      pkg.isActive
                        ? "bg-teal-500/20 text-teal-300 border-teal-400/40 hover:bg-teal-500/30"
                        : "bg-gray-800 text-gray-400 border-gray-700 hover:text-white"
                    }`}
                    title={pkg.isActive ? "Deactivate plan" : "Activate plan"}
                  >
                    <FiPower size={14} />
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(pkg)}
                      className="p-2 rounded-xl text-xs text-cyan-300 bg-cyan-950/60 border border-cyan-400/30 hover:bg-cyan-900/60 transition"
                      title="Edit plan"
                    >
                      <FiEdit size={14} />
                    </button>
                    {isSuperAdmin && (
                      <button
                        onClick={() => {
                          setPlanToDelete(pkg);
                          setConfirmModalOpen(true);
                        }}
                        className="p-2 rounded-xl text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 hover:bg-rose-900/40 transition"
                        title="Delete plan"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
          <div className="w-full max-w-2xl rounded-3xl overflow-hidden border border-cyan-400/30 bg-gradient-to-b from-[#061e47]/95 to-[#020b22]/98 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">
                {editingId ? "Edit Plan" : "Create Plan"}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-lg text-cyan-400 hover:bg-cyan-950/50 transition"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              {/* Category Selection */}
              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Plan Category *</label>
                <select
                  disabled={Boolean(editingId)}
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition disabled:opacity-50"
                >
                  <option value="Entry">Entry Membership</option>
                  <option value="Call_Pearls">Call Pearls</option>
                  <option value="Boost_Badge">Boost Badge</option>
                  <option value="Perfect_Match">Perfect Match</option>
                </select>
              </div>

              {/* Plan Name */}
              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Plan Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. 700 Audio Pearls / Premium Monthly"
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                />
              </div>

              {/* Pricing */}
              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Selling Price (₹) *</label>
                <input
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="299"
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Original Price (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={form.originalPrice}
                  onChange={(e) => setForm({ ...form, originalPrice: e.target.value })}
                  placeholder="599"
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                />
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                >
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>

              <div>
                <label className="block text-cyan-200 mb-2 font-semibold">Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: e.target.value })}
                  placeholder="0"
                  className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                />
              </div>
              <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">For Gender *</label>
                    <select
                      value={form.forGender}
                      onChange={(e) => setForm({ ...form, forGender: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="all">All</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="both">Both(male and Female)</option>
                    </select>
                  </div>

              {/* ----------------- ENTRY SPECIFIC FIELDS ----------------- */}
              {form.category === "Entry" && (
                <>
                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-bold mb-3">Entry Plan Specifications</p>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Duration *</label>
                    <select
                      value={form.durationInDays}
                      onChange={(e) => setForm({ ...form, durationInDays: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="7">Weekly (7 days)</option>
                      <option value="30">Monthly (30 days)</option>
                      <option value="90">Quarterly (90 days)</option>
                      <option value="365">Yearly (365 days)</option>
                    </select>
                  </div>

                  

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Matches / Day</label>
                    <input
                      type="number"
                      min="0"
                      value={form.personalizedPremiumMatchesPerDay}
                      onChange={(e) =>
                        setForm({ ...form, personalizedPremiumMatchesPerDay: e.target.value })
                      }
                      placeholder="10"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-cyan-200 mb-2 font-semibold">Profile Boosts / Week</label>
                    <input
                      type="number"
                      min="0"
                      value={form.profileBoostersPerWeek}
                      onChange={(e) =>
                        setForm({ ...form, profileBoostersPerWeek: e.target.value })
                      }
                      placeholder="1"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-semibold mb-3">Communication Features</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.unlimitedChats}
                          onChange={(e) => setForm({ ...form, unlimitedChats: e.target.checked })}
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Unlimited Chats</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.unlimitedLikes}
                          onChange={(e) => setForm({ ...form, unlimitedLikes: e.target.checked })}
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Unlimited Likes</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ----------------- CALL PEARLS SPECIFIC FIELDS ----------------- */}
              {form.category === "Call_Pearls" && (
                <>
                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-bold mb-3">Call Pearls Configuration</p>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Call Type Focus *</label>
                    <select
                      value={form.callTypeFocus}
                      onChange={(e) => setForm({ ...form, callTypeFocus: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="Audio">Audio Only</option>
                      <option value="Video">Video Only</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Pearls to Credit *</label>
                    <input
                      type="number"
                      min="1"
                      value={form.pearlsToCredit}
                      onChange={(e) => setForm({ ...form, pearlsToCredit: e.target.value })}
                      placeholder="700"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Estimated Talk Minutes</label>
                    <input
                      type="number"
                      min="0"
                      value={form.estimatedMinutes}
                      onChange={(e) => setForm({ ...form, estimatedMinutes: e.target.value })}
                      placeholder="10"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Pearls Per Minute Required</label>
                    <input
                      type="number"
                      min="0"
                      value={form.pearlsPerMinuteRequired}
                      onChange={(e) =>
                        setForm({ ...form, pearlsPerMinuteRequired: e.target.value })
                      }
                      placeholder="70"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>
                </>
              )}

              {/* ----------------- BOOST BADGE SPECIFIC FIELDS ----------------- */}
              {form.category === "Boost_Badge" && (
                <>
                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-bold mb-3">Boost Badge Configuration</p>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Badge Tier *</label>
                    <select
                      value={form.badgeTier}
                      onChange={(e) => setForm({ ...form, badgeTier: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="Mini">Mini</option>
                      <option value="Super">Super</option>
                      <option value="Power">Power</option>
                      <option value="Elite">Elite</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Boost Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={form.boostQuantity}
                      onChange={(e) => setForm({ ...form, boostQuantity: e.target.value })}
                      placeholder="5"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Duration Per Boost (Days) *</label>
                    <input
                      type="number"
                      min="1"
                      value={form.durationPerBoostInDays}
                      onChange={(e) => setForm({ ...form, durationPerBoostInDays: e.target.value })}
                      placeholder="3"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Discover Placement</label>
                    <select
                      value={form.discoverPlacement}
                      onChange={(e) => setForm({ ...form, discoverPlacement: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="priority">Priority</option>
                      <option value="standard">Standard</option>
                      <option value="top">Top Spotlight</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-semibold mb-3">Boost Perks</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.visibleBoostBadge}
                          onChange={(e) =>
                            setForm({ ...form, visibleBoostBadge: e.target.checked })
                          }
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Visible Badge on Profile</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.increasedProfileVisibility}
                          onChange={(e) =>
                            setForm({ ...form, increasedProfileVisibility: e.target.checked })
                          }
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Increased Profile Visibility</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* ----------------- PERFECT MATCH SPECIFIC FIELDS ----------------- */}
              {form.category === "Perfect_Match" && (
                <>
                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-bold mb-3">Perfect Match Configuration</p>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Duration *</label>
                    <select
                      value={form.durationInDays}
                      onChange={(e) => setForm({ ...form, durationInDays: e.target.value })}
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    >
                      <option value="7">Weekly (7 days)</option>
                      <option value="30">Monthly (30 days)</option>
                      <option value="90">Quarterly (90 days)</option>
                      <option value="365">Yearly (365 days)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-cyan-200 mb-2 font-semibold">Daily Perfect Matches *</label>
                    <input
                      type="number"
                      min="1"
                      value={form.dailyPerfectMatchAllowance}
                      onChange={(e) => setForm({ ...form, dailyPerfectMatchAllowance: e.target.value })}
                      placeholder="5"
                      className="w-full bg-cyan-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white outline-none focus:border-cyan-300 transition"
                    />
                  </div>

                  <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4">
                    <p className="text-cyan-300 font-semibold mb-3">Perfect Match Features</p>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.priorityMatching}
                          onChange={(e) =>
                            setForm({ ...form, priorityMatching: e.target.checked })
                          }
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Priority Matching</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-cyan-200 hover:text-white transition">
                        <input
                          type="checkbox"
                          checked={form.advancedFilters}
                          onChange={(e) =>
                            setForm({ ...form, advancedFilters: e.target.checked })
                          }
                          className="rounded accent-cyan-400 w-4 h-4"
                        />
                        <span>Advanced Filters</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Status & Highlights */}
              <div className="sm:col-span-2 border-t border-cyan-500/20 pt-4 flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer text-cyan-200">
                  <input
                    type="checkbox"
                    checked={form.isPopular}
                    onChange={(e) => setForm({ ...form, isPopular: e.target.checked })}
                    className="rounded accent-cyan-400 w-4 h-4"
                  />
                  <span>Mark as Popular (★)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-cyan-200">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="rounded accent-cyan-400 w-4 h-4"
                  />
                  <span>Live & Active</span>
                </label>
              </div>
            </div>

            {/* Modal Controls */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-cyan-500/20">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-cyan-950/60 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-900/60 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePlan}
                disabled={saving}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg hover:brightness-110 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "Save Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmModalOpen && planToDelete && (
        <ConfirmModal
          isOpen={confirmModalOpen}
          title="Delete Subscription Plan"
          message={`Are you sure you want to delete "${planToDelete.name}"? This action cannot be undone.`}
          block="Yes, Delete"
          onConfirm={() => handleDeletePlan(planToDelete)}
          onCancel={() => {
            setConfirmModalOpen(false);
            setPlanToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default SubscriptionPlans;